import { ab } from './interface'
import {
  Experiment,
  AudioHighlight982Variant,
  TrialByPlanV2_18369Variant,
  IosExternalLinkPayment16218Variant,
  Streaks16464Variant,
  NullExperiment17503Variant,
  FirstGuideActivation17817Variant,
  TimeToRead17958Variant,
  FreeUserUpgrade18151Variant,
  PasswordlessSignup18304Variant,
  OnboardingV8SurveyPersonalizationRedesign18060Variant,
  BillingTrialAnnualUpsell18236Variant,
} from './types'

import { runTextUpdates } from '@/services/ab/13448_ab_experiment'
import Cookies from 'js-cookie'
import { platform } from '@/services/platform'
import { platformData } from '@/services/platform.capacitor'
import { getIosStorefrontLocation } from '@/services/capacitor.storefrontLocationPlugin'
import { pushABExperimentsToDataLayer } from '@/analytics/gtm'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'

/**
 * Initialize CloudFront experiments.
 *
 * Check A/B testing cookies set by CloudFront, send backend
 * requests and update frontend a/b data storage.
 * Pushes AB experiments to GTM data layer when done.
 */
export async function initAB(): Promise<void> {
  await newCopy13448Experiment(Experiment.new_copy_pdf_v2_13448, '/pdf')
  await aiGuideFlow13747()
  pushABExperimentsToDataLayer()
}

/**
 * Feature flag to enable/disable Google Billing. Disabled by default.
 *
 * This is not a real A/B experiment, i.e. the experiment is not activated on
 * the backend.
 */
export function googleBillingEnabled(): boolean {
  return ab.experimentVariant(Experiment.google_billing_7517) === 'yes'
}

/**
 * Apply the text changes on the public pages
 *
 * @param {Experiment} experiment - The A/B experiment containing the text changes.
 * @param {string} allowedUrlExpression - A regular expression to match allowed URLs,
 * on which the text changes should be applied.
 */
export async function newCopy13448Experiment(
  experiment: Experiment,
  allowedUrlExpression: string,
): Promise<void> {
  const pathname = window.location.pathname
  const isTranslatedPage = pathname.includes(`/es${allowedUrlExpression}/`)

  if (!pathname.includes(allowedUrlExpression) || isTranslatedPage) {
    // Only start experiment on non-translated pdf pages
    return
  }

  let result = ab.experimentVariant(experiment)

  if (result === null) {
    result = await ab.start(experiment)
  }

  if (result === 'yes') {
    runTextUpdates(experiment)
  }
}

/**
 * Highlight the text on summary pages along with the audio being played.
 */
export function audioHighlight982ExperimentVariant(): AudioHighlight982Variant {
  return ab.experimentVariant(
    Experiment.exp_audio_highlight_982,
  ) as AudioHighlight982Variant
}

/**
 * Highlight the text on summary pages along with the audio being played.
 */
export async function audioHighlight982Experiment(): Promise<AudioHighlight982Variant> {
  let result = ab.experimentVariant(Experiment.exp_audio_highlight_982)

  if (result === null) {
    result = await ab.start(Experiment.exp_audio_highlight_982)
  }

  return result as AudioHighlight982Variant
}

/**
 * Get the variant of the ai_label_13537 experiment.
 * Returns boolean:
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export function aiLabel13537ExperimentVariant(): boolean {
  return ab.experimentVariant(Experiment.ai_label_13537) === 'yes'
}

/**
 * Decide wheter or not to render the ai content label component in
 * the book preview and summary pages.
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export async function aiLabel13537Experiment(): Promise<boolean> {
  let result = ab.experimentVariant(Experiment.ai_label_13537)

  if (result === null) {
    result = await ab.start(Experiment.ai_label_13537)
  }

  return result === 'yes'
}

/*
 * Read experiment cookies set by CloudFront and transfer the experiment assignment to the local storage
 */
export async function aiGuideFlow13747(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-AI-Guide-Flow-13747')
  const localVariant = ab.experimentVariant(Experiment.ai_guide_flow_13747)

  if (cfVariant && !localVariant) {
    await ab.start(Experiment.ai_guide_flow_13747, cfVariant)
  }
}

/**
 * Trial by Plan V2 experiment.
 *
 * A - Control: current experience without the trial by plan feature.
 * B - Variant: monthly and annual plans have different trial lengths
 *     (no trial for monthly, 14 days for annual).
 */
export async function trialByPlanV2_18369Experiment(): Promise<TrialByPlanV2_18369Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_trial_by_plan_v2_18369,
  ) as TrialByPlanV2_18369Variant

  const isAndroid = await platform.isAndroid()
  const isNativeIOSApp = await platform.isNativeIOSApp()

  if (isAndroid || isNativeIOSApp) {
    return null
  }

  if (result === null) {
    result = (await ab.start(
      Experiment.exp_trial_by_plan_v2_18369,
    )) as TrialByPlanV2_18369Variant
  }

  return result
}

export function trialByPlanV2_18369Variant(): TrialByPlanV2_18369Variant {
  return ab.experimentVariant(
    Experiment.exp_trial_by_plan_v2_18369,
  ) as TrialByPlanV2_18369Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export async function iosExternalLinkPayment16218Experiment(): Promise<IosExternalLinkPayment16218Variant> {
  await platformData.init()
  const appBuild = platformData.appBuild
  // We need to get the storefront location for the experiment
  // and it's not available in builds < 60.
  if (appBuild < 60) {
    return null
  }
  const storefrontLocation = await getIosStorefrontLocation()
  // External payments are allowed in the US storefront only.
  if (storefrontLocation !== 'USA') {
    return null
  }

  // Check variant in the local storage
  let result = ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  )

  // Start the experiment
  if (result === null) {
    result = await ab.start(Experiment.exp_ios_external_link_payment_16218)
  }

  return result as IosExternalLinkPayment16218Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export function iosExternalLinkPayment16218Variant(): IosExternalLinkPayment16218Variant {
  return ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  ) as IosExternalLinkPayment16218Variant
}

/**
 * Start the streaks_16464 experiment.
 *
 * A - Control, current logic without Streaks.
 * B - Version with the Streaks feature and generic push notifications.
 * C - Version with the Streaks feature and personalized push notifications.
 */
export async function streaks16464Experiment(): Promise<Streaks16464Variant> {
  let result = ab.experimentVariant(Experiment.exp_streaks_16464)

  if (result === null) {
    result = await ab.start(Experiment.exp_streaks_16464)
  }

  return result as Streaks16464Variant
}

/**
 * Get the variant for the streaks_16464 experiment.
 */
export function streaks16464Variant(): Streaks16464Variant {
  return ab.experimentVariant(
    Experiment.exp_streaks_16464,
  ) as Streaks16464Variant
}

/**
 * Shortcut to check whether to display streaks_16464 features.
 *
 * null, A: no
 * B, C: yes (the front-end display is the same for B and C)
 */
export function streaks16464ShowStreaks(): boolean {
  const variant = streaks16464Variant()
  return variant === 'B' || variant === 'C'
}

/**
 * Start the exp_null_experiment_17503 experiment on signup page visit.
 *
 * A - Control, no changes.
 * B - Variant, no changes.
 */
export async function signupPageNullExperiment17503Experiment(): Promise<NullExperiment17503Variant> {
  let result = ab.experimentVariant(Experiment.exp_null_experiment_17503)

  if (result === null) {
    result = await ab.start(Experiment.exp_null_experiment_17503)
  }

  return result as NullExperiment17503Variant
}

/**
 * Start the exp_first_guide_activation_17817 experiment at the end of
 * onboarding.
 *
 * A - Control: existing flow after onboarding.
 * B - Variant: display "first guide" popup after onboarding.
 */
export async function firstGuideActivation17817Experiment(): Promise<FirstGuideActivation17817Variant> {
  let result = ab.experimentVariant(Experiment.exp_first_guide_activation_17817)

  if (result === null) {
    result = await ab.start(Experiment.exp_first_guide_activation_17817)
  }

  return result as FirstGuideActivation17817Variant
}

/**
 * Get the variant for the exp_first_guide_activation_17817 experiment.
 */
export function firstGuideActivation17817Variant(): FirstGuideActivation17817Variant {
  return ab.experimentVariant(
    Experiment.exp_first_guide_activation_17817,
  ) as FirstGuideActivation17817Variant
}

/**
 * Start the exp_time_to_read_17958 experiment from the Layout component.
 *
 * A - Control: no time estimates.
 * B - Variant: display time estimates on doc cards.
 */
export async function timeToRead17958Experiment(): Promise<TimeToRead17958Variant> {
  let result = ab.experimentVariant(Experiment.exp_time_to_read_17958)

  if (result === null) {
    result = await ab.start(Experiment.exp_time_to_read_17958)
  }

  return result as TimeToRead17958Variant
}

/**
 * Get the variant for the exp_time_to_read_17958 experiment.
 */
export function timeToRead17958Variant(): TimeToRead17958Variant {
  return ab.experimentVariant(
    Experiment.exp_time_to_read_17958,
  ) as TimeToRead17958Variant
}

/**
 * Start the exp_free_user_upgrade_18151 when navigating from the billing page.
 *
 * We exclude these users from the experiment;
 * - users created after the experiment started,
 * - users that already started the free trial,
 * - paid users.
 *
 * A - Control: Current experience. No redirect, no modal, CTAs unchanged.
 * B - Variant: With the following changes:
 * - return-to-billing redirect on every return visit until trial start,
 * - one-time modal triggered on first page navigation off the billing page,
 * - nav CTA copy updated from "Free Trial" to "Start Free Trial",
 * - gated content CTA copy updated from "Subscribe to Read Full Summary" to "Start Free Trial to Unlock"
 */
export async function freeUserUpgrade18151Experiment(): Promise<FreeUserUpgrade18151Variant> {
  let result = ab.experimentVariant(Experiment.exp_free_user_upgrade_18151)

  if (result === null) {
    result = await ab.start(Experiment.exp_free_user_upgrade_18151)
  }

  if (result === 'B') {
    storage.setItem(StorageKeys.upgradePromptModal18151, 'true')
  }

  return result as FreeUserUpgrade18151Variant
}

/**
 * Get the variant for the exp_free_user_upgrade_18151 experiment.
 */
export function freeUserUpgrade18151Variant(): FreeUserUpgrade18151Variant {
  const result = ab.experimentVariant(Experiment.exp_free_user_upgrade_18151)

  return result as FreeUserUpgrade18151Variant
}

/**
 * Start the exp_onboarding_v8_survey_personalization_redesign_18060 experiment.
 * Kept as a no-op after rollout so signup code can stay unchanged without
 * sending an assignment request to the backend.
 *
 * A - Control: existing onboarding survey flow.
 * B - Variant: redesigned onboarding survey flow.
 */
export async function onboardingPersonalization18060Experiment(): Promise<void> {}

/**
 * Get the variant for the exp_onboarding_v8_survey_personalization_redesign_18060 experiment.
 */
export function onboardingPersonalization18060Variant(): OnboardingV8SurveyPersonalizationRedesign18060Variant {
  const result = ab.experimentVariant(
    Experiment.exp_onboarding_v8_survey_personalization_redesign_18060,
  ) as OnboardingV8SurveyPersonalizationRedesign18060Variant

  return result ?? 'B'
}

/**
 * Start the exp_passwordless_signup_18304 experiment.
 *
 * A - Control: existing signup flow with password.
 * B - Variant: passwordless signup flow.
 */
export async function passwordlessSignup18304Experiment(): Promise<PasswordlessSignup18304Variant> {
  let result = ab.experimentVariant(Experiment.exp_passwordless_signup_18304)

  if (result === null) {
    result = await ab.start(Experiment.exp_passwordless_signup_18304)
  }

  return result as PasswordlessSignup18304Variant
}

/**
 * Get the variant for the exp_passwordless_signup_18304 experiment.
 */
export function passwordlessSignup18304Variant(): PasswordlessSignup18304Variant {
  return ab.experimentVariant(
    Experiment.exp_passwordless_signup_18304,
  ) as PasswordlessSignup18304Variant
}

/**
 * Start the exp_billing_trial_annual_upsell_18236 experiment in any page (Layout component)
 * after more than 5 page visits.
 *
 * A - Control: existing flow with no popup and banner.
 * B - Variant: annual upsell prompt popup and banner.
 */
export async function billingTrialAnnualUpsell18236Experiment(): Promise<BillingTrialAnnualUpsell18236Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_billing_trial_annual_upsell_18236,
  )

  if (result === null) {
    result = await ab.start(Experiment.exp_billing_trial_annual_upsell_18236)

    if (result === 'B') {
      storage.setItem(StorageKeys.annualUpsellPromptModal18236, 'true')
      storage.setItem(StorageKeys.annualUpsellPromptBanner18236, 'true')
    }
  }

  return result as BillingTrialAnnualUpsell18236Variant
}

/**
 * Get the variant for the exp_free_user_upgrade_18151 experiment.
 */
export function billingTrialAnnualUpsell18236Variant(): BillingTrialAnnualUpsell18236Variant {
  const result = ab.experimentVariant(
    Experiment.exp_billing_trial_annual_upsell_18236,
  )

  return result as BillingTrialAnnualUpsell18236Variant
}
